import {useEffect, useMemo} from "react";
import {useUrlBackground} from "hooks/useBackgroundNumbers";
import {useQueries, useQuery, useQueryClient} from "@tanstack/react-query";
import {API} from "api";
import {useImages, BG_TRANSFORM_FIELDS} from "contexts/images";
import {useSetting} from "contexts/setting";
import {getHashes, selectBackgrounds} from "libs/bg-utils";
import {useParams} from "react-router-dom";
import {DEFAULT_BACKGROUND_DATA} from "data/backgrounds";
import {getBackgroundsQueryKey} from "hooks/useBgData";
import {catalogHasStoredTransforms} from "libs/bgTransform";

function hasSceneBgTransforms(scene) {
  if (!scene) return false;
  return BG_TRANSFORM_FIELDS.some((field) => Object.keys(scene[field] ?? {}).length > 0);
}

function sceneToBgTransform(scene) {
  if (!hasSceneBgTransforms(scene)) return null;
  return {
    x: scene.bgX,
    y: scene.bgY,
    scale: scene.bgScale,
    flip: scene.bgFlip ?? null,
    rotate: scene.bgRotate ?? null,
  };
}

/**
 * Resolve bg transforms directly from overrides.scene_data.
 * Priority per background index:
 *   1) product + set + background
 *   2) product + set
 *   3) * + set + background
 *   4) * + set
 *
 * If a matched override has no bg transform fields, resolution keeps searching.
 */
export function buildTransformsFromOverrides(overrides, channel, type, productCode, set, bgCount) {
  if (!overrides?.length || !bgCount) return {};

  const normalizedSet = set || "";

  const findSceneForBg = (product, bgStr) => {
    const candidates = [{background: bgStr}, {background: null}];

    for (const candidate of candidates) {
      const row = overrides.find(
        (o) =>
          o.channel === channel &&
          o.product === product &&
          o.type === type &&
          (o.set || "") === normalizedSet &&
          !o.layout &&
          (candidate.background ? (o.background || "") === candidate.background : !o.background)
      );

      if (!row?.scene_data) continue;
      if (!hasSceneBgTransforms(row.scene_data)) continue;
      return row.scene_data;
    }

    return null;
  };

  const result = {};
  const defaultTransforms = [];
  const productTransforms = [];

  for (let i = 1; i <= bgCount; i += 1) {
    const bgStr = String(i);

    const productScene =
      productCode && productCode !== "ALL" ? findSceneForBg(productCode, bgStr) : null;
    const defaultScene = findSceneForBg("*", bgStr);

    productTransforms.push(sceneToBgTransform(productScene));
    defaultTransforms.push(sceneToBgTransform(defaultScene));
  }

  if (defaultTransforms.some(Boolean)) {
    result.default = defaultTransforms;
  }

  if (productCode && productCode !== "ALL" && productTransforms.some(Boolean)) {
    result[productCode] = productTransforms;
  }

  return result;
}

export function combineBgTransformQueries(results) {
  const data = results.reduce((acc, curr) => {
    if (curr.data) {
      acc[curr.data.productCode] = curr.data.transforms;
    }
    return acc;
  }, {});

  return {
    data,
    pending: results.some((result) => result.isPending),
    isError: results.some((result) => result.isError),
    error: results.find((result) => result.isError)?.error ?? null,
    retry: () => Promise.all(results.map((result) => result.refetch())),
    // No queries needed = trivially successful (not "pending forever")
    success: results.length > 0 ? results.every((result) => result.isSuccess) : true,
  };
}

/**
 * Pick the authoritative background source: a caller-supplied override, or the
 * remote tileset query.
 *
 * `overrideStatus` is for overrides that are themselves asynchronous — the v3
 * catalog under bgDerivation. Without it an override is taken as synchronous
 * local data (Ads reads a bundled manifest), so it reports ready immediately and
 * never inherits the disabled remote query's failure. With it, the override's
 * own pending/error state flows through, because "loading" and "failed" must not
 * both render as "this channel has no backgrounds".
 */
export function resolveBackgroundsSource(query, backgroundsOverride, overrideStatus = null) {
  if (backgroundsOverride !== null) {
    if (overrideStatus) {
      return {
        backgrounds: backgroundsOverride,
        isPending: overrideStatus.isPending,
        isSuccess: overrideStatus.isSuccess,
        isError: overrideStatus.isError,
        error: overrideStatus.error,
        retry: overrideStatus.retry,
      };
    }
    return {
      backgrounds: backgroundsOverride,
      isPending: false,
      isSuccess: true,
      isError: false,
      error: null,
      retry: async () => [],
    };
  }

  return {
    backgrounds: query.data,
    isPending: query.isPending,
    isSuccess: query.isSuccess,
    isError: query.isError,
    error: query.error,
    retry: query.refetch,
  };
}

export function shouldFetchRemoteBackgrounds(channel, type, enabled, backgroundsOverride) {
  return Boolean(channel && type && enabled && backgroundsOverride === null);
}

export function makeBackgroundsQueryData(backgrounds) {
  return {
    tile_sets: Object.fromEntries(backgrounds.map((urls, index) => [index + 1, urls])),
  };
}

export function getBgTransformKeys(hashes, productCode, disabled = false) {
  if (disabled) return [];
  return ["default", productCode].filter(
    (key) => key && Array.isArray(hashes[key]) && hashes[key].length > 0
  );
}

const useBgTransforms = (hashes, channel, productCode, disabled = false) => {
  const keysToFetch = getBgTransformKeys(hashes, productCode, disabled);

  const queries = useQueries({
    refetchOnWindowFocus: false,
    queries: keysToFetch.map((key) => ({
      queryKey: ["bgTransforms", channel, key, hashes[key], hashes.default],
      refetchOnWindowFocus: false,
      queryFn: () => {
        let preparedHashes = [...hashes[key]];
        const defaultHashes = hashes.default || [];
        for (let i = 0; i < preparedHashes.length; i += 1) {
          if (!preparedHashes[i]) {
            preparedHashes[i] = defaultHashes[i] || "null";
          }
        }

        return API.fetchScenes(preparedHashes).then((scenes) => ({
          productCode: key,
          transforms: scenes.map((scene, i) =>
            scene
              ? {
                  x: scene.bgX,
                  y: scene.bgY,
                  scale: scene.bgScale,
                  flip: scene.bgFlip ?? null,
                  rotate: scene.bgRotate ?? null,
                  hash: preparedHashes[i],
                }
              : null
          ),
        }));
      },
    })),
    combine: combineBgTransformQueries,
  });

  return {
    data: queries.data,
    isPending: queries.pending,
    isSuccess: queries.success,
    isError: queries.isError,
    error: queries.error,
    retry: queries.retry,
  };
};

/**
 * The crop table of a saved link's gallery (App's `linkGallery`): the crop of
 * each background for the product, at the index of the legacy table
 * (variation - 1). A link offers these backgrounds, not the legacy tiles, so a
 * pick gets the crop of the image its tile shows. A background without a
 * stored crop is not in the table: the legacy fallback table applies to it,
 * as on the product page (useEffectiveBgTransform).
 */
export function linkGalleryTransforms(gallery, productCode) {
  const table = [];
  for (const background of gallery) {
    if (!catalogHasStoredTransforms(background, productCode)) continue;
    const x = {};
    const y = {};
    const scale = {};
    const rotate = {};
    const flip = {};
    for (const [id, transform] of Object.entries(background.transforms)) {
      x[id] = transform.x;
      y[id] = transform.y;
      scale[id] = transform.scale;
      rotate[id] = transform.rotate ?? 0;
      flip[id] = transform.flip ?? {x: false, y: false};
    }
    table[background.variation - 1] = {x, y, scale, rotate, flip};
  }
  return {[productCode]: table};
}

/**
 * Merge override transforms with hash-based transforms.
 * Per background index: override wins if present, otherwise fall back to hash.
 */
function mergeTransforms(overrideTransforms, hashTransforms) {
  const result = {};
  const allKeys = new Set([...Object.keys(overrideTransforms), ...Object.keys(hashTransforms)]);
  for (const key of allKeys) {
    const overrides = overrideTransforms[key] || [];
    const hashes = hashTransforms[key] || [];
    const maxLen = Math.max(overrides.length, hashes.length);
    result[key] = Array.from({length: maxLen}, (_, i) => overrides[i] ?? hashes[i] ?? null);
  }
  return result;
}

const DEFAULT_TYPE = "general";

export function useBackgrounds(
  channel,
  type,
  product,
  {
    sceneHasBgTransforms = false,
    isInitialSceneLoaded = true,
    overrides = null,
    set = null,
    hasRouteDefaultScene = false,
    hasInitialSceneTransforms = false,
    initialSceneBgOption = null,
    // True only when an override actually applies for the current
    // (channel, product, type, set, layout, bg). Single source of truth —
    // useInitialScene owns the resolution; this hook trusts it. Two
    // independent definitions of "is an override active" cannot coexist:
    // they diverge whenever overrides exist for sibling products but not
    // the current one, and route the current product into an override
    // effect that never applies.
    hasOverrides = false,
    // Some channels own their background source locally. When supplied, this
    // is authoritative and the remote tileset query must not run or fail load.
    backgroundsOverride = null,
    // Status of an *asynchronous* override (the v3 catalog under bgDerivation):
    // {isPending, isSuccess, isError, error, retry}. Omit for synchronous local
    // sources like Ads. See resolveBackgroundsSource.
    backgroundsOverrideStatus = null,
    // External gate to hold off the initial get_tilesets fetch. Lets callers
    // defer until channel is known to match the loaded scene (on /scene/:id
    // routes channel context starts wrong and gets corrected post-load).
    enabled: enabledGate = true,
    // App owns this route-level decision because not every v3-hosted channel
    // has catalog rows. Keeping one authoritative gate prevents this hook from
    // disabling the legacy transform path while App expects it to remain live.
    bgDerivation = false,
    // A saved link's gallery: the published list of the scene's product, or
    // null where the link keeps the legacy tiles (see App). A pick on the link
    // gets its crop from it, after the default-scene rows of the route.
    linkGallery = null,
    // A selected saved-scene background loads its whole appearance together.
    sceneBackgroundSelection = false,
  } = {}
) {
  const {sceneId} = useParams();
  const {editTransform} = useImages();
  const {bgOption, hash} = useSetting();
  // Phase-2 (issue #528, 2a): when on, the effective-transform selector owns
  // the displayed/saved crop — this hook must NOT push tile/override transforms
  // into the images context (two of the three racing writers live below). The
  // background LIST it returns is unchanged; only the apply-effects are gated.
  // Scoped to non-saved routes (see App.js): saved scenes keep the legacy path.
  const urlBgOption = useUrlBackground().background;

  const currentType = type ?? DEFAULT_TYPE;
  const isAllProductsMode = hasOverrides && product.value === "ALL";

  const hasBackgroundsOverride = backgroundsOverride !== null;
  const queryClient = useQueryClient();
  const backgroundsQueryKey = useMemo(
    () => getBackgroundsQueryKey(channel, currentType),
    [channel, currentType]
  );
  const overrideQueryData = useMemo(
    () => (hasBackgroundsOverride ? makeBackgroundsQueryData(backgroundsOverride) : undefined),
    [hasBackgroundsOverride, backgroundsOverride]
  );
  const backgroundsQuery = useQuery({
    queryKey: backgroundsQueryKey,
    queryFn: () => API.fetchBackgrounds(channel, currentType),
    select: selectBackgrounds,
    enabled: shouldFetchRemoteBackgrounds(channel, currentType, enabledGate, backgroundsOverride),
    initialData: overrideQueryData,
    refetchOnWindowFocus: false,
  });
  useEffect(() => {
    if (overrideQueryData) queryClient.setQueryData(backgroundsQueryKey, overrideQueryData);
  }, [backgroundsQueryKey, overrideQueryData, queryClient]);
  const {
    backgrounds,
    isPending: isPendingBackgrounds,
    isSuccess: isSuccessBackgrounds,
    isError: isErrorBackgrounds,
    error: backgroundsError,
    retry: retryBackgrounds,
  } = resolveBackgroundsSource(backgroundsQuery, backgroundsOverride, backgroundsOverrideStatus);

  const fileHashes = useMemo(() => {
    return isSuccessBackgrounds ? getHashes(backgrounds, product.value) : {};
  }, [backgrounds, isSuccessBackgrounds, product.value]);

  const {
    isSuccess: isSuccessHashTransforms,
    isPending: isPendingHashTransforms,
    data: hashTransforms,
    isError: isErrorHashTransforms,
    error: hashTransformsError,
    retry: retryHashTransforms,
    // Phase-2 (issue #528, 2a): under bgDerivation the crop comes from the
    // selector (catalog + resolved), so the per-tile transform scenes are never
    // read — skip the POST /scene/load/v2 fan-out entirely (the 1.78 MB
    // offender in the issue's evidence table). Its output (`transforms`) feeds
    // only BgSelector.transformBg, which is a no-op under the flag. A link
    // with a gallery reads the gallery's crops instead.
  } = useBgTransforms(
    fileHashes,
    channel,
    product.value,
    hasBackgroundsOverride || bgDerivation || linkGallery !== null
  );
  const galleryTransforms = useMemo(
    () => (linkGallery ? linkGalleryTransforms(linkGallery, product.value) : null),
    [linkGallery, product.value]
  );
  const tileTransforms = galleryTransforms ?? hashTransforms;

  const releaseTransforms = useMemo(() => {
    if (!hasOverrides || !isSuccessBackgrounds || !backgrounds?.length) return {};
    // The rows of every background the link's gallery offers, also of one
    // numbered after the last legacy tile.
    const bgCount = Math.max(
      backgrounds.length,
      ...(linkGallery ?? []).map((background) => background.variation)
    );
    return buildTransformsFromOverrides(
      overrides,
      channel,
      currentType,
      product.value,
      set,
      bgCount
    );
  }, [
    hasOverrides,
    isSuccessBackgrounds,
    backgrounds,
    linkGallery,
    overrides,
    channel,
    currentType,
    product.value,
    set,
  ]);
  const hasOverrideTransformData = useMemo(() => {
    return Object.values(releaseTransforms).some((arr) => Array.isArray(arr) && arr.some(Boolean));
  }, [releaseTransforms]);

  const transforms = useMemo(() => {
    return hasOverrides ? mergeTransforms(releaseTransforms, tileTransforms) : tileTransforms;
  }, [hasOverrides, releaseTransforms, tileTransforms]);
  // Releases: don't block rendering on hash queries — overrides are the primary source,
  // hashes are a fallback that fills in asynchronously. Non-releases: must wait for hashes.
  const isPendingTransforms = hasOverrides ? false : isPendingHashTransforms;

  // --- Non-Releases: hash-based background transform flow (legacy/production) ---
  useEffect(() => {
    if (bgDerivation || sceneBackgroundSelection) return;
    if (hasBackgroundsOverride) return;
    if (hasOverrides) return;
    if (!isSuccessHashTransforms) return;

    // Livestream renders its background from customBgs (the default gradient SVG
    // + per-template crop transforms, or a custom upload), not from the gallery
    // tileset transforms. editTransform() replaces the whole bgX/bgY/bgScale
    // record, which would wipe the seeded per-template crop transforms — so skip
    // the gallery-transform rehydration for this channel.
    if (channel === "livestream") return;

    if (sceneId || bgOption === "custom") return;
    if (hasRouteDefaultScene && !isInitialSceneLoaded) return;

    const shouldPreferInitialSceneTransforms =
      hasRouteDefaultScene &&
      hasInitialSceneTransforms &&
      String(bgOption) === String(initialSceneBgOption ?? "1");

    // Route-default scenes already carry tuned transforms for the initial bg option.
    // Rehydrating from background metadata would overwrite those persisted values.
    if (shouldPreferInitialSceneTransforms) return;

    const optionIndex = Number(bgOption) - 1;
    const isValidIndex = Number.isFinite(optionIndex) && optionIndex >= 0;
    const productTransforms = transforms?.[product.value];
    const defaultTransforms = transforms?.default;

    let coords = null;
    if (isValidIndex && productTransforms?.[optionIndex]) {
      coords = productTransforms[optionIndex];
    } else if (isValidIndex && defaultTransforms?.[optionIndex]) {
      coords = defaultTransforms[optionIndex];
    }

    const {x, y, scale, rotate, flip} = coords || DEFAULT_BACKGROUND_DATA;
    editTransform(x, y, scale, rotate, flip);
  }, [
    hasOverrides,
    hasBackgroundsOverride,
    isSuccessHashTransforms,
    backgrounds,
    transforms,
    sceneId,
    hash,
    bgOption,
    product.value,
    channel,
    editTransform,
    bgDerivation,
    sceneBackgroundSelection,
  ]);

  // --- Releases: override transforms with hash-based fallback ---
  useEffect(() => {
    if (bgDerivation || sceneBackgroundSelection) return;
    if (hasBackgroundsOverride) return;
    if (!hasOverrides) return;
    if (!isSuccessBackgrounds) return;

    if (!isSuccessHashTransforms && !hasOverrideTransformData) return;
    if (!isInitialSceneLoaded) return;
    if (sceneId && hash !== sceneId) return;
    if (sceneId && !urlBgOption) return;
    if (bgOption === "custom") return;
    // ALL mode uses per-product aggregated transforms from initialSceneData.
    // Do not overwrite them with single-product transform resolution.
    if (isAllProductsMode) return;

    // Scene transforms take priority only when bg is not explicitly chosen in URL.
    // If `background` is present, the selected background transform from overrides
    // must win (same behavior as clicking a bg tile in the toolbar).
    if (!sceneId && hash && sceneHasBgTransforms && !urlBgOption) return;

    const resolvedBgOption = urlBgOption ?? String(bgOption);
    if (!resolvedBgOption || resolvedBgOption === "custom") return;

    const optionIndex = Number(resolvedBgOption) - 1;
    const isValidIndex = Number.isFinite(optionIndex) && optionIndex >= 0;
    const productTransforms = transforms?.[product.value];
    const defaultTransforms = transforms?.default;

    let coords = null;
    if (isValidIndex && productTransforms?.[optionIndex]) {
      coords = productTransforms[optionIndex];
    } else if (isValidIndex && defaultTransforms?.[optionIndex]) {
      coords = defaultTransforms[optionIndex];
    }

    const {x, y, scale, rotate, flip} = coords || DEFAULT_BACKGROUND_DATA;
    editTransform(x, y, scale, rotate, flip);
  }, [
    hasOverrides,
    hasBackgroundsOverride,
    isSuccessBackgrounds,
    isSuccessHashTransforms,
    hasOverrideTransformData,
    backgrounds,
    transforms,
    urlBgOption,
    hash,
    sceneHasBgTransforms,
    isInitialSceneLoaded,
    isAllProductsMode,
    bgOption,
    product.value,
    sceneId,
    editTransform,
    hasRouteDefaultScene,
    hasInitialSceneTransforms,
    initialSceneBgOption,
    bgDerivation,
    sceneBackgroundSelection,
  ]);

  return {
    backgrounds,
    transforms,
    isPending: isPendingBackgrounds || isPendingTransforms,
    isError: isErrorBackgrounds || isErrorHashTransforms,
    error: backgroundsError || hashTransformsError,
    retry: () => Promise.all([retryBackgrounds(), retryHashTransforms()]),
  };
}
